// IntegratedMenuFormModal.jsx — 통합 메뉴 생성/수정 모달 (Phase 1-1b)
//   2026-10-01 (통합 관리 홈, 시안대로): 제목 "통합 메뉴 편집 — 이름", "사이드바에 보일 이름", 사이드바 묶음(보여 주기만),
//   회사 둥근 단추 + 회사를 더하면 경고, 배정 N명·배정하러 가기, 같이 묶인 메뉴, 삭제는 아래 왼쪽,
//   정렬 칸은 글자 칸(화살표 금지) + 숫자만. 조각은 IntegratedMenuFormExtras.jsx.
//   initialCodes — 기능 × 회사 표의 "+" 로 열면 그 회사를 미리 켠다(저장은 사람이 누른다).
import React, { useEffect, useState } from 'react';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';
// 기능 이름표는 한 곳에서 (2026-10-01)
import { INTEGRATED_FEATURES, integratedFeatureLabel } from './integratedFeatureMeta';
import { CompanyPills, GroupHint, AssigneeBox, PairedBox } from './IntegratedMenuFormExtras';
import { displayMenuName } from '../adminlayout/integratedScope';
import { MENU_GROUPS } from '../adminlayout/menuConfig';
import { regroupMenuGroups, useMenuCategories } from '../adminlayout/menuCategories';

const SORT_ORDER_RE = /^-?\d{0,6}$/; // 정렬 칸: 숫자만(글자가 들어가면 서버가 500 — NaN)

// 자동 제안: 회사 정렬 + feature 라벨 결합 (예: "PINE+DOT 연차 관리").
//   2026-10-15: 회사는 작업 대상과 같은 이름으로("유기자차+무기자차 계약 관리") — 이름을 모르면 코드.
function suggestName(codes, featureType, nameOf = (c) => c) {
  if (!Array.isArray(codes) || codes.length < 2) return '';
  const featureLabel = integratedFeatureLabel(featureType);
  return `${[...codes].sort().map(nameOf).join('+')} ${featureLabel}`.trim();
}

// allowedFeatures(2026-10-15): 고를 수 있는 기능(서버 allowed_features — 열쇠 보유자는 자기 테넌트 예산). null 이면 전부.
export function IntegratedMenuFormModal({
  open, onClose, onSubmit, onDelete = null, editing, companies, initialCodes = null, menus = null, allowedFeatures = null,
}) {
  const isEdit = Boolean(editing?.id);
  const featureOptions = INTEGRATED_FEATURES.filter((ft) => !Array.isArray(allowedFeatures) || allowedFeatures.includes(ft.value)
    || (isEdit && ft.value === editing?.feature_type));
  const defaultFeature = featureOptions[0]?.value || 'leave';
  const nameOf = (code) => (companies || []).find((c) => String(c.code).toUpperCase() === String(code).toUpperCase())?.name || code;
  useBodyScrollLock(open);
  const menuGroups = regroupMenuGroups(MENU_GROUPS, useMenuCategories());
  const [name, setName] = useState('');
  const [featureType, setFeatureType] = useState('leave');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [sortOrder, setSortOrder] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const overlayCloseProps = useOverlayClickClose(onClose);

  // ── 초기값 로드 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || '');
      setFeatureType(editing.feature_type || 'leave');
      setSelectedCodes(Array.isArray(initialCodes) ? [...initialCodes]
        : (Array.isArray(editing.company_codes) ? [...editing.company_codes] : []));
      setSortOrder(String(editing.sort_order || 0));
      setNameTouched(true); // 편집 모드는 자동 제안 안 함
    } else {
      setName(''); setFeatureType(defaultFeature); setSelectedCodes([]); setSortOrder('0'); setNameTouched(false);
    }
    setSubmitting(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, initialCodes]);

  // ── 회사 체크 시 이름 자동 제안 (사용자 직접 수정 전까지만) ────────────
  useEffect(() => {
    if (nameTouched) return;
    setName(suggestName(selectedCodes, featureType, nameOf));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCodes, featureType, nameTouched]);

  if (!open) return null;

  function toggleCode(code) {
    setSelectedCodes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  }

  async function handleSubmit() {
    if (!name.trim()) { alert('이름은 필수입니다.'); return; }
    if (selectedCodes.length < 2) { alert('회사를 최소 2개 이상 선택해 주세요.'); return; }
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        feature_type: featureType,
        company_codes: selectedCodes,
        sort_order: Number(sortOrder) || 0,
      });
    } finally { setSubmitting(false); }
  }

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>📋</div>
            <h3 style={{ margin: 0, ...stdModalHeaderTitle }}>
              {isEdit ? `통합 메뉴 편집 — ${displayMenuName(editing.name)}` : '새 통합 메뉴'}
            </h3>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>×</button>
        </div>
        <div style={modalBodyStyle}>
          <Field label="사이드바에 보일 이름 *">
            <input
              type="text" style={inputStyle} value={name}
              placeholder="예: PINE+DOT 연차 관리"
              onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
              maxLength={100}
            />
            <div style={helperTextStyle}>앞의 * 는 사이드바·통합 관리 홈에서 빼고 보여요.</div>
          </Field>
          <Field label="기능 *">
            <select style={inputStyle} value={featureType} onChange={(e) => setFeatureType(e.target.value)} disabled={isEdit}>
              {featureOptions.map((ft) => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
            {isEdit && <div style={helperTextStyle}>기능은 만든 뒤 바꿀 수 없습니다.</div>}
          </Field>
          <Field label="사이드바 묶음">
            <GroupHint featureType={featureType} menuGroups={menuGroups} />
          </Field>
          <Field label="묶을 회사 (2곳 이상) *">
            <CompanyPills
              companies={companies}
              selected={selectedCodes}
              original={editing?.company_codes || []}
              assignees={editing?.assignees_count}
              isEdit={isEdit}
              onToggle={toggleCode}
            />
            <div style={helperTextStyle}>선택된 회사: {selectedCodes.length}개 (최소 2개 필요)</div>
          </Field>
          <Field label="정렬 순서">
            <input
              type="text" inputMode="numeric" style={inputStyle} value={sortOrder}
              onChange={(e) => { if (SORT_ORDER_RE.test(e.target.value)) setSortOrder(e.target.value); }}
            />
          </Field>
          {isEdit && <AssigneeBox count={editing.assignees_count} />}
          {isEdit && <PairedBox editing={editing} menus={menus} selected={selectedCodes} />}
        </div>
        <div style={{ ...modalFooterStyle, justifyContent: 'space-between' }}>
          <span>
            {isEdit && onDelete && (
              <button type="button" onClick={() => onDelete(editing)} style={deleteBtnStyle} disabled={submitting}>삭제</button>
            )}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={btnStyle} disabled={submitting}>취소</button>
            <button onClick={handleSubmit} style={primaryBtnStyle} disabled={submitting}>
              {submitting ? '저장 중...' : '저장'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

// ── 스타일 (다크모드 대응 CSS 변수 사용) ─────────────────────────────────
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalStyle = { background: 'var(--bg, #fff)', color: 'var(--text, #111)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' };
// 모달 헤더 — 디자인 표준: 검정 밴드 (카드 라운드 12에 맞춤)
const modalHeaderStyle = stdModalHeaderBar(12);
const modalBodyStyle = { padding: 20 };
const modalFooterStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border, #eee)' };
const closeBtnStyle = stdModalCloseBtn;
const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-muted, #555)' };
const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border, #ddd)', borderRadius: 6, background: 'var(--bg-card, #fff)', color: 'var(--text, #111)', boxSizing: 'border-box' };
const helperTextStyle = { fontSize: 11, color: 'var(--text-muted, #888)', marginTop: 4 };
const btnStyle = { padding: '8px 16px', fontSize: 13, border: '1px solid var(--border, #ddd)', borderRadius: 6, background: 'var(--bg-subtle, #f5f5f5)', color: 'var(--text, #111)', cursor: 'pointer' };
const primaryBtnStyle = { ...btnStyle, background: '#3b82f6', color: '#fff', borderColor: '#3b82f6', fontWeight: 600 };
const deleteBtnStyle = { ...btnStyle, background: 'var(--bg-card, #fff)', color: '#b91c1c', borderColor: '#fecaca' };
